import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { strapiAxios, strapiAdminAxios } from "@/lib/crm/strapi-client";

interface User {
  id: number;
  username: string;
  email: string;
  firstname?: string;
  lastname?: string;
  isActive?: boolean;
  blocked?: boolean;
  roles?: Array<{
    id: number;
    name: string;
    code: string;
    description: string;
  }>;
  role: {
    id: number;
    name: string;
    type: string;
    code?: string;
  };
}

interface LoginCredentials {
  identifier: string; // email or username
  password: string;
}

interface LoginResponse {
  jwt: string;
  user: User;
}

/**
 * Hook for CRM authentication
 * Manages login, logout, and user state
 */
export function useCRMAuth() {
  const queryClient = useQueryClient();

  // Get current user from token
  const { data: user, isLoading, error } = useQuery<User | null>({
    queryKey: ["crm-auth-user"],
    queryFn: async () => {
      const token = localStorage.getItem("strapi-jwt-token");

      if (!token) {
        return null;
      }

      try {
        const response = await strapiAdminAxios.get("/admin/users/me");

        // Transform admin user response to expected format
        const adminUser = response.data.data;
        console.log("🔍 Admin user data fetched:", adminUser);

        const transformedUser = {
          id: adminUser.id,
          username: adminUser.firstname || adminUser.email.split('@')[0],
          email: adminUser.email,
          firstname: adminUser.firstname,
          lastname: adminUser.lastname,
          isActive: adminUser.isActive,
          blocked: adminUser.blocked,
          roles: adminUser.roles,
          role: {
            id: adminUser.roles?.[0]?.id || 0,
            name: adminUser.roles?.[0]?.name || "Admin",
            type: "admin",
            code: adminUser.roles?.[0]?.code || "strapi-admin"
          }
        };

        console.log("🔍 Transformed user:", transformedUser);
        return transformedUser;
      } catch (error: any) {
        console.error("❌ Error fetching admin user:", error);
        // Token invalid or expired
        localStorage.removeItem("strapi-jwt-token");
        localStorage.removeItem("strapi-user-type");
        return null;
      }
    },
    retry: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!localStorage.getItem("strapi-jwt-token"), // Only run if token exists
  });

  // Login mutation - ONLY admin users allowed
  const loginMutation = useMutation<LoginResponse, Error, LoginCredentials>({
    mutationFn: async (credentials) => {
      // ONLY admin authentication - no frontend fallback
      const response = await strapiAdminAxios.post("/admin/login", {
        email: credentials.identifier,
        password: credentials.password,
      });

      console.log("🔍 Admin login response:", response.data);

      // Transform admin response to match expected format
      const adminData = response.data.data;
      return {
        jwt: adminData.token,
        user: {
          id: adminData.user.id,
          username: adminData.user.firstname || adminData.user.email.split('@')[0],
          email: adminData.user.email,
          firstname: adminData.user.firstname,
          lastname: adminData.user.lastname,
          isActive: adminData.user.isActive,
          blocked: adminData.user.blocked,
          roles: adminData.user.roles,
          role: {
            id: adminData.user.roles?.[0]?.id || 0,
            name: adminData.user.roles?.[0]?.name || "Admin",
            type: "admin",
            code: adminData.user.roles?.[0]?.code || "strapi-admin"
          }
        }
      };
    },
    onSuccess: (data) => {
      // Store JWT token
      localStorage.setItem("strapi-jwt-token", data.jwt);
      localStorage.setItem("strapi-user-type", "admin");

      console.log("✅ Login successful, user:", data.user);

      // Update user in cache
      queryClient.setQueryData(["crm-auth-user"], data.user);
    },
  });

  // Logout mutation
  const logoutMutation = useMutation({
    mutationFn: async () => {
      // Strapi doesn't have server-side logout, just clear local state
      localStorage.removeItem("strapi-jwt-token");
    },
    onSuccess: () => {
      // Clear user from cache
      queryClient.setQueryData(["crm-auth-user"], null);

      // Optionally clear all CRM-related queries
      queryClient.invalidateQueries({ queryKey: ["crm"] });
    },
  });

  // Computed states
  const isAuthenticated = !!user && !!localStorage.getItem("strapi-jwt-token");
  const userType = localStorage.getItem("strapi-user-type");

  // ONLY admin users allowed - simplified check for any admin user
  const isAgent = isAuthenticated && userType === "admin";
  const isAdmin = isAuthenticated && userType === "admin";

  // Debug logging
  console.log("🔍 Auth State:", {
    isAuthenticated,
    isAgent,
    isAdmin,
    userType,
    roleType: user?.role?.type,
    roleCode: user?.role?.code,
    roles: user?.roles,
    user
  });

  return {
    user,
    isLoading,
    error,
    isAuthenticated,
    isAgent,
    isAdmin,
    login: loginMutation.mutate,
    loginAsync: loginMutation.mutateAsync,
    isLoggingIn: loginMutation.isPending,
    loginError: loginMutation.error,
    logout: logoutMutation.mutate,
    logoutAsync: logoutMutation.mutateAsync,
    isLoggingOut: logoutMutation.isPending,
  };
}

/**
 * Higher-order function to protect routes
 * Usage: <ProtectedRoute><YourComponent /></ProtectedRoute>
 */
export function useRequireAuth() {
  const { isAuthenticated, isAgent, isLoading } = useCRMAuth();

  return {
    isAuthenticated,
    isAgent,
    isLoading,
    canAccessCRM: isAuthenticated && isAgent,
  };
}
